// PDF Offer Generator — premium quality output matching top payment providers (Adyen, Stripe).
// Uses jsPDF + jspdf-autotable for professional tables and layout.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Brand colors
const NAVY = [27, 43, 91];       // #1B2B5B
const GREEN = [0, 200, 83];      // #00C853
const SLATE = [71, 85, 105];     // #475569
const LIGHT = [248, 250, 252];   // slate-50

// Company defaults (can be overridden via settings)
const COMPANY_DEFAULT = {
  name: "AyaPay",
  legal: "AyaPay by AYAPOS",
  address: "Chemin des Lilas 12",
  postalCity: "2503 Biel/Bienne",
  country: "Suisse",
  email: "contact@ayapay.ch",
  phone: "+41 76 200 36 12",
  website: "https://ayapay.ch",
  logoUrl: "https://customer-assets.emergentagent.com/job_payment-links-hub-1/artifacts/qmtwufa4_test%20logo%20.jpg",
};

// Load image as base64 (for logo)
const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL("image/png"), width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = url;
  });

export async function generateOfferPdf(offer) {
  const company = { ...COMPANY_DEFAULT, ...(offer.company || {}) };
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;

  // ==== HEADER — logo + company block ====
  let logoAdded = false;
  try {
    const { dataUrl, width, height } = await loadImage(company.logoUrl);
    const logoH = 22; // Increased from 14 for better visibility
    const logoW = (width / height) * logoH;
    doc.addImage(dataUrl, "PNG", margin, margin, logoW, logoH);
    logoAdded = true;
  } catch { /* no logo, fallback to text */ }

  if (!logoAdded) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(26);
    doc.setTextColor(...NAVY);
    doc.text("AyaPay", margin, margin + 12);
  }

  // Company info right-aligned
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...SLATE);
  const rightX = pageW - margin;
  const infoLines = [
    company.legal,
    company.address,
    `${company.postalCity}, ${company.country}`,
    company.phone,
    company.email,
  ];
  infoLines.forEach((line, i) => {
    doc.text(line, rightX, margin + 4 + i * 4, { align: "right" });
  });

  // Green accent line under header — placed with generous clearance from text descenders
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.6);
  doc.line(margin, margin + 30, pageW - margin, margin + 30);

  // ==== TITLE BLOCK ====
  let y = margin + 42;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...NAVY);
  doc.text("Offre commerciale", margin, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE);
  doc.text(`N° ${offer.offerNumber}`, rightX, y - 8, { align: "right" });
  doc.text(`Émise le ${offer.issueDate}`, rightX, y - 4, { align: "right" });
  doc.text(`Valable jusqu'au ${offer.validUntil}`, rightX, y, { align: "right" });

  y += 10;

  // ==== CLIENT BLOCK ====
  doc.setFillColor(...LIGHT);
  doc.roundedRect(margin, y, pageW - 2 * margin, 34, 2, 2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text("DESTINATAIRE", margin + 4, y + 6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(20, 25, 40);
  doc.text(offer.client.company || "-", margin + 4, y + 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE);
  const clientRows = [
    offer.client.contact ? `À l'attention de ${offer.client.contact}` : null,
    offer.client.address || null,
    [offer.client.postalCode, offer.client.city, offer.client.country].filter(Boolean).join(", ") || null,
    [offer.client.email, offer.client.phone].filter(Boolean).join("  ·  ") || null,
    offer.client.ide ? `IDE : ${offer.client.ide}` : null,
  ].filter(Boolean);
  clientRows.forEach((line, i) => doc.text(line, margin + 4, y + 18 + i * 3.7));

  y += 42;

  // ==== INTRO PARAGRAPH ====
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...SLATE);
  const introDefault = `Suite à nos échanges, nous avons le plaisir de vous transmettre notre proposition commerciale pour l'équipement de votre commerce avec la solution de paiement AyaPay. Notre offre comprend la mise à disposition gratuite des terminaux, l'assurance, la maintenance, la SIM 5G et le support 24/7.`;
  const introText = offer.intro || introDefault;
  const introLines = doc.splitTextToSize(introText, pageW - 2 * margin);
  doc.text(introLines, margin, y);
  y += introLines.length * 4.5 + 4;

  // ==== RATES TABLE ====
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  const modelLabel = offer.rateModel === "icpp" ? "Interchange++ (IC++)" : "Full Blend";
  doc.text(`Grille tarifaire — Modèle ${modelLabel}`, margin, y);
  y += 2;

  autoTable(doc, {
    startY: y + 2,
    columns: [
      { header: "Moyen de paiement", dataKey: "method" },
      { header: "Taux (%)", dataKey: "rate" },
      { header: "Frais fixes (CHF)", dataKey: "fixed" },
      { header: "Notes", dataKey: "notes" },
    ],
    body: (offer.rates || []).map((r) => ({
      method: r.method,
      rate: r.rate ? `${r.rate} %` : "-",
      fixed: r.fixed ? `${r.fixed} CHF` : "-",
      notes: r.notes || "",
    })),
    styles: { font: "helvetica", fontSize: 9, cellPadding: 3, textColor: [30, 30, 45] },
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
    alternateRowStyles: { fillColor: [246, 248, 252] },
    columnStyles: {
      rate: { halign: "right", cellWidth: 25 },
      fixed: { halign: "right", cellWidth: 30 },
    },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 3;

  // Small legend explaining the chosen pricing model
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.setTextColor(...SLATE);
  const legend = offer.rateModel === "icpp"
    ? "Modèle IC++ : facturation basée sur Interchange (banque émettrice) + Scheme fees (Visa/MC) + Marge acquéreur. Détails de composition disponibles sur demande."
    : "Modèle Full Blend : un taux unique par moyen de paiement, facturation simple et prévisible.";
  const legendLines = doc.splitTextToSize(legend, pageW - 2 * margin);
  doc.text(legendLines, margin, y);
  y += legendLines.length * 3 + 4;

  // ==== EQUIPMENT TABLE ====
  if (offer.equipment && offer.equipment.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    doc.text("Équipement mis à disposition", margin, y);

    autoTable(doc, {
      startY: y + 4,
      columns: [
        { header: "Modèle", dataKey: "model" },
        { header: "Description", dataKey: "description" },
        { header: "Quantité", dataKey: "qty" },
        { header: "Prix unitaire", dataKey: "price" },
      ],
      body: offer.equipment.map((e) => ({
        model: e.model,
        description: e.description || "",
        qty: e.qty || 1,
        price: e.price === 0 || e.price === "0" ? "GRATUIT" : `${e.price} CHF`,
      })),
      styles: { font: "helvetica", fontSize: 9, cellPadding: 3, textColor: [30, 30, 45] },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
      alternateRowStyles: { fillColor: [246, 248, 252] },
      columnStyles: {
        qty: { halign: "center", cellWidth: 20 },
        price: { halign: "right", cellWidth: 30, textColor: GREEN, fontStyle: "bold" },
      },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Ensure not overflowing — add page if needed
  if (y > pageH - 70) {
    doc.addPage();
    y = margin + 5;
  }

  // ==== CONDITIONS ====
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text("Conditions générales de l'offre", margin, y);
  y += 5;

  const conds = offer.conditions || [
    "Engagement contractuel : 1 an, avec 1 mois d'essai gratuit.",
    "Frais d'activation unique : CHF 199.- (paramétrage compte marchand, configuration terminal, formation initiale).",
    "Délai de versement des fonds : J+2 (jours ouvrés) sur votre compte bancaire suisse.",
    "Terminal, assurance, maintenance, SIM 5G et support 24/7 inclus.",
    "Conformité PCI-DSS Niveau 1 · FINMA · LPD / RGPD.",
    "Aucun frais caché · Résiliation possible à l'échéance sans engagement.",
  ];
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE);
  conds.forEach((c, i) => {
    doc.setTextColor(...GREEN);
    doc.text("•", margin + 1, y + i * 5);
    doc.setTextColor(...SLATE);
    const lines = doc.splitTextToSize(c, pageW - 2 * margin - 6);
    doc.text(lines, margin + 5, y + i * 5);
  });
  y += conds.length * 5 + 4;

  // ==== NOTES ====
  if (offer.notes) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text("Notes complémentaires", margin, y);
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...SLATE);
    const notesLines = doc.splitTextToSize(offer.notes, pageW - 2 * margin);
    doc.text(notesLines, margin, y);
    y += notesLines.length * 4.2 + 4;
  }

  // ==== SIGNATURE BLOCK ====
  const sigY = Math.max(y + 8, pageH - 45);
  doc.setDrawColor(...SLATE);
  doc.setLineWidth(0.2);
  doc.line(margin, sigY, margin + 60, sigY);
  doc.line(pageW - margin - 60, sigY, pageW - margin, sigY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SLATE);
  doc.text("Signature client · Bon pour accord", margin, sigY + 4);
  doc.text("AyaPay · " + (offer.salesRep || "Emrah AYAZ"), pageW - margin, sigY + 4, { align: "right" });

  // ==== FOOTER ====
  const footerY = pageH - 10;
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.4);
  doc.line(margin, footerY - 5, pageW - margin, footerY - 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...SLATE);
  doc.text(`${company.legal} · ${company.address} · ${company.postalCity} · ${company.website}`, pageW / 2, footerY, { align: "center" });
  doc.setTextColor(...GREEN);
  doc.text("PCI-DSS Niveau 1  ·  FINMA compliant  ·  Swiss Made", pageW / 2, footerY - 2.5, { align: "center" });

  // Page numbering (if multiple)
  const pageCount = doc.getNumberOfPages();
  if (pageCount > 1) {
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(...SLATE);
      doc.text(`${i} / ${pageCount}`, pageW - margin, footerY + 3, { align: "right" });
    }
  }

  return doc;
}

// Convenience: generate + download
export async function downloadOfferPdf(offer) {
  const doc = await generateOfferPdf(offer);
  const filename = `offre-${offer.offerNumber || "ayapay"}-${(offer.client?.company || "client").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
  doc.save(filename);
}

// Convenience: generate + open in new tab (preview)
export async function previewOfferPdf(offer) {
  const doc = await generateOfferPdf(offer);
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}
